import React, { useEffect, useState, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { MapPin, ArrowLeft, Play, Pause, Loader2, MessageCircle, Send, CheckCircle2, Circle } from 'lucide-react';
import { MapContainer, TileLayer, Marker } from 'react-leaflet';
import { toast } from 'sonner';
import L from 'leaflet';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const treeIcon = new L.Icon({
  iconUrl: 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIzMiIgaGVpZ2h0PSIzMiIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSJub25lIiBzdHJva2U9IiMxQTNDMzQiIHN0cm9rZS13aWR0aD0iMiIgc3Ryb2tlLWxpbmVjYXA9InJvdW5kIiBzdHJva2UtbGluZWpvaW49InJvdW5kIj48cGF0aCBkPSJNMTIgMnYyMCIvPjxwYXRoIGQ9Im0xNy41IDE2LjUtNSAzLTUtM00xNyAxMmwtNSAzLTUtM00xNyA3bC01IDMtNS0zIi8+PC9zdmc+',
  iconSize: [32, 32],
  iconAnchor: [16, 32],
});

const TreeDetail = () => {
  const { id } = useParams();
  const [tree, setTree] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentAudio, setCurrentAudio] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef(null);
  
  // AI Companion state
  const [showAICompanion, setShowAICompanion] = useState(false);
  const [aiMessages, setAiMessages] = useState([]);
  const [userInput, setUserInput] = useState('');
  const [isAILoading, setIsAILoading] = useState(false);
  const [maturityMode, setMaturityMode] = useState('foundation');
  const [perspectivesHeard, setPerspectivesHeard] = useState([]);
  
  // Found trees state
  const [isFound, setIsFound] = useState(false);

  useEffect(() => {
    // Check if this tree is marked as found
    const saved = localStorage.getItem('foundTrees');
    if (saved) {
      const foundTrees = JSON.parse(saved);
      setIsFound(foundTrees.includes(id));
    }
  }, [id]);

  const toggleFound = () => {
    const saved = localStorage.getItem('foundTrees');
    let foundTrees = saved ? JSON.parse(saved) : [];
    
    if (isFound) {
      foundTrees = foundTrees.filter(treeId => treeId !== id);
      toast.success(`${tree.name} unmarked`);
    } else {
      foundTrees.push(id);
      toast.success(`${tree.name} found! 🌳`);
    }
    
    setIsFound(!isFound);
    localStorage.setItem('foundTrees', JSON.stringify(foundTrees));
  };

  useEffect(() => {
    const fetchTree = async () => {
      try {
        const response = await axios.get(`${API}/trees/${id}`);
        setTree(response.data);
      } catch (error) {
        console.error('Error fetching tree:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchTree();
  }, [id]);

  const handlePlayAudio = (audioTrack) => {
    if (currentAudio?.type === audioTrack.type && isPlaying) {
      audioRef.current?.pause();
      setIsPlaying(false);
    } else {
      if (audioRef.current) {
        audioRef.current.src = audioTrack.url;
        audioRef.current.play();
        setIsPlaying(true);
        setCurrentAudio(audioTrack);
        
        // Track perspectives heard for AI companion
        if (!perspectivesHeard.includes(audioTrack.type)) {
          setPerspectivesHeard([...perspectivesHeard, audioTrack.type]);
        }
      }
    }
  };
  
  const initializeAICompanion = async () => {
    setShowAICompanion(true);
    setIsAILoading(true);
    
    try {
      const response = await axios.post(`${API}/trees/${id}/ai-companion`, {
        tree_id: id,
        perspectives_heard: perspectivesHeard,
        maturity_mode: maturityMode,
        conversation_history: [],
        user_message: null
      });
      
      setAiMessages([
        {
          role: 'assistant',
          content: response.data.response
        }
      ]);
    } catch (error) {
      console.error('Error initializing AI companion:', error);
      setAiMessages([
        {
          role: 'assistant',
          content: 'I\'m here to listen and reflect with you. What draws you to this tree?'
        }
      ]);
    } finally {
      setIsAILoading(false);
    }
  };
  
  const sendAIMessage = async () => {
    if (!userInput.trim()) return;
    
    const newUserMessage = {
      role: 'user',
      content: userInput
    };
    
    setAiMessages([...aiMessages, newUserMessage]);
    setUserInput('');
    setIsAILoading(true);
    
    try {
      const response = await axios.post(`${API}/trees/${id}/ai-companion`, {
        tree_id: id,
        perspectives_heard: perspectivesHeard,
        maturity_mode: maturityMode,
        conversation_history: [...aiMessages, newUserMessage],
        user_message: userInput
      });
      
      setAiMessages([
        ...aiMessages,
        newUserMessage,
        {
          role: 'assistant',
          content: response.data.response
        }
      ]);
    } catch (error) {
      console.error('Error sending AI message:', error);
      setAiMessages([
        ...aiMessages,
        newUserMessage,
        {
          role: 'assistant',
          content: 'I\'m having trouble connecting right now. But I\'m here with you. Take your time.'
        }
      ]);
    } finally {
      setIsAILoading(false);
    }
  };

  useEffect(() => {
    const audio = audioRef.current;
    if (audio) {
      const handleEnded = () => setIsPlaying(false);
      audio.addEventListener('ended', handleEnded);
      return () => audio.removeEventListener('ended', handleEnded);
    }
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-4rem)]" data-testid="tree-detail-loading">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!tree) {
    return (
      <div className="flex flex-col items-center justify-center h-[calc(100vh-4rem)]" data-testid="tree-not-found">
        <h2 className="text-2xl font-heading font-bold text-primary mb-4">Tree not found</h2>
        <Link to="/gallery" className="text-accent hover:underline">
          Return to Gallery
        </Link>
      </div>
    );
  }

  const audioTrackTypes = [
    // Indigenous & Cultural (Earth tones - browns, ochre)
    { type: 'indigenous_elders', label: 'Indigenous Elders & Knowledge Holders', color: 'bg-[#8B4513]' },
    { type: 'cultural_custodians', label: 'Cultural Custodians', color: 'bg-[#A0522D]' },
    // Scientific & Research (Deep greens, teals - analytical)
    { type: 'ecologists', label: 'Ecologists', color: 'bg-[#1A3C34]' },
    { type: 'botanists', label: 'Botanists', color: 'bg-[#2D5016]' },
    { type: 'mycorrhizal', label: 'Mycorrhizal Researchers', color: 'bg-[#3E5C2E]' },
    { type: 'climate_scientists', label: 'Climate Scientists', color: 'bg-[#1E4D2B]' },
    { type: 'fire_ecologists', label: 'Fire Ecologists', color: 'bg-[#B7410E]' },
    { type: 'hydrologists', label: 'Hydrologists', color: 'bg-[#2C5F6F]' },
    // Practitioners & Land Workers (Working tones - browns, greens)
    { type: 'arborists', label: 'Arborists', color: 'bg-[#4F772D]' },
    { type: 'rangers', label: 'Rangers', color: 'bg-[#556B2F]' },
    { type: 'land_managers', label: 'Land Managers', color: 'bg-[#6B8E23]' },
    { type: 'timber_workers', label: 'Timber Workers', color: 'bg-[#654321]' },
    { type: 'agroforesters', label: 'Agroforesters', color: 'bg-[#5F8A3D]' },
    // Community & Humanities (Warm, accessible tones)
    { type: 'historians', label: 'Historians', color: 'bg-[#704214]' },
    { type: 'local_community', label: 'Local Community', color: 'bg-[#8B7355]' },
    { type: 'children', label: 'Children', color: 'bg-[#D4A373]' },
    { type: 'artists', label: 'Artists', color: 'bg-[#9B6B4F]' },
    { type: 'faith_leaders', label: 'Faith Leaders', color: 'bg-[#8B6F47]' },
    { type: 'philosophers', label: 'Philosophers', color: 'bg-[#6B5D4F]' },
    { type: 'poets', label: 'Poets', color: 'bg-[#8FA69D]' },
    // Policy & Advocacy (Professional tones - navy, grey-blue)
    { type: 'planners', label: 'Planners', color: 'bg-[#4A5568]' },
    { type: 'lawyers', label: 'Lawyers', color: 'bg-[#2D3748]' },
    { type: 'rights_advocates', label: 'Rights-of-Nature Advocates', color: 'bg-[#2F5233]' },
  ];

  return (
    <div className="min-h-screen" data-testid="tree-detail-page">
      <audio ref={audioRef} />
      
      {/* Header */}
      <div className="border-b border-border bg-white/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Link
              to="/gallery"
              className="inline-flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-primary transition-colors"
              data-testid="back-to-gallery-link"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Gallery
            </Link>
            
            {/* Found Tree Button */}
            <button
              onClick={toggleFound}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border-2 transition-all hover:shadow-md"
              style={{
                borderColor: isFound ? '#10b981' : '#e5e7eb',
                backgroundColor: isFound ? '#f0fdf4' : 'white',
                color: isFound ? '#10b981' : '#6b7280'
              }}
            >
              {isFound ? (
                <>
                  <CheckCircle2 className="w-5 h-5" />
                  <span className="text-sm font-medium">Found!</span>
                </>
              ) : (
                <>
                  <Circle className="w-5 h-5" />
                  <span className="text-sm font-medium">Mark as Found</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Split Screen Layout */}
      <div className="grid lg:grid-cols-2 min-h-[calc(100vh-8rem)]">
        {/* Left: Fixed Image */}
        <div className="relative lg:sticky lg:top-16 h-[50vh] lg:h-[calc(100vh-4rem)]">
          <img
            src={tree.image_url}
            alt={tree.name}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-primary/60 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-8 text-white">
            <h1 className="text-4xl sm:text-5xl font-heading font-bold mb-2" data-testid="tree-name">
              {tree.name}
            </h1>
            <p className="text-lg italic opacity-90" data-testid="tree-scientific-name">
              {tree.scientific_name}
            </p>
          </div>
        </div>

        {/* Right: Scrollable Content */}
        <div className="bg-white p-8 lg:p-12 space-y-12">
          {/* Basic Info */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            data-testid="tree-basic-info"
          >
            <div className="grid grid-cols-2 gap-4 p-6 border border-border rounded-lg bg-secondary/30">
              <div>
                <h3 className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Family</h3>
                <p className="font-medium text-primary">{tree.family}</p>
              </div>
              <div>
                <h3 className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Habitat</h3>
                <p className="font-medium text-primary">{tree.habitat}</p>
              </div>
              {tree.city && (
                <div>
                  <h3 className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Location</h3>
                  <p className="font-medium text-primary">{tree.city}</p>
                </div>
              )}
              <div className={tree.city ? '' : 'col-span-2'}>
                <h3 className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Conservation Status</h3>
                <p className="font-medium text-primary">{tree.conservation_status}</p>
              </div>
            </div>
          </motion.section>

          {/* Description */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            data-testid="tree-description"
          >
            <h2 className="text-2xl font-heading font-bold text-primary mb-4">Description</h2>
            <p className="text-base text-foreground leading-relaxed">{tree.description}</p>
          </motion.section>

          {/* Science Details */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            data-testid="tree-science-details"
          >
            <h2 className="text-2xl font-heading font-bold text-primary mb-4">Scientific Details</h2>
            <p className="text-base text-foreground leading-relaxed">{tree.science_details}</p>
          </motion.section>

          {/* Audio Tracks */}
          {tree.audio_tracks && tree.audio_tracks.length > 0 && (
            <motion.section
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              data-testid="tree-audio-tracks"
            >
              <h2 className="text-2xl font-heading font-bold text-primary mb-4">Audio Perspectives</h2>
              <div className="audio-player-container">
                {audioTrackTypes.map((trackType) => {
                  const track = tree.audio_tracks.find((t) => t.type === trackType.type);
                  if (!track) return null;
                  
                  const isActive = currentAudio?.type === track.type && isPlaying;
                  
                  return (
                    <button
                      key={track.type}
                      onClick={() => handlePlayAudio(track)}
                      className={`audio-track-button ${isActive ? 'active' : ''}`}
                      data-testid={`audio-track-${track.type}`}
                    >
                      <div className={`w-10 h-10 rounded-full ${trackType.color} flex items-center justify-center flex-shrink-0`}>
                        {isActive ? (
                          <Pause className="w-5 h-5 text-white" />
                        ) : (
                          <Play className="w-5 h-5 text-white" />
                        )}
                      </div>
                      <div className="flex-1 text-left">
                        <div className="font-medium">{trackType.label}</div>
                        {track.title && <div className="text-xs opacity-75">{track.title}</div>}
                      </div>
                    </button>
                  );
                })}
              </div>
            </motion.section>
          )}

          {/* AI Companion Section */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.45 }}
            data-testid="ai-companion-section"
          >
            <div className="border-t border-border pt-12">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-2xl font-heading font-bold text-primary">Tree Museum AI</h2>
                <button
                  onClick={initializeAICompanion}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
                  data-testid="start-ai-companion"
                >
                  <MessageCircle className="w-4 h-4" />
                  {showAICompanion ? 'Restart Companion' : 'Start Reflection'}
                </button>
              </div>
              
              <p className="text-sm text-muted-foreground mb-4">
                A quiet guide for reflection. Not here to teach, but to listen.
              </p>

              {showAICompanion && (
                <div className="space-y-4">
                  {/* Maturity Mode Selector */}
                  <div className="flex gap-2">
                    <button
                      onClick={() => setMaturityMode('foundation')}
                      className={`px-3 py-1 text-sm rounded-full transition-colors ${
                        maturityMode === 'foundation'
                          ? 'bg-primary text-white'
                          : 'bg-secondary text-foreground hover:bg-secondary/80'
                      }`}
                    >
                      Foundation
                    </button>
                    <button
                      onClick={() => setMaturityMode('depth')}
                      className={`px-3 py-1 text-sm rounded-full transition-colors ${
                        maturityMode === 'depth'
                          ? 'bg-primary text-white'
                          : 'bg-secondary text-foreground hover:bg-secondary/80'
                      }`}
                    >
                      Depth
                    </button>
                    <button
                      onClick={() => setMaturityMode('legacy')}
                      className={`px-3 py-1 text-sm rounded-full transition-colors ${
                        maturityMode === 'legacy'
                          ? 'bg-primary text-white'
                          : 'bg-secondary text-foreground hover:bg-secondary/80'
                      }`}
                    >
                      Legacy
                    </button>
                  </div>

                  {/* Chat Messages */}
                  <div className="bg-secondary/30 rounded-lg p-6 space-y-4 min-h-[300px] max-h-[500px] overflow-y-auto">
                    {aiMessages.map((message, index) => (
                      <div
                        key={index}
                        className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                      >
                        <div
                          className={`max-w-[80%] rounded-lg p-4 ${
                            message.role === 'user'
                              ? 'bg-primary text-white'
                              : 'bg-white border border-border text-foreground'
                          }`}
                        >
                          <p className="text-sm leading-relaxed">{message.content}</p>
                        </div>
                      </div>
                    ))}
                    
                    {isAILoading && (
                      <div className="flex justify-start">
                        <div className="bg-white border border-border rounded-lg p-4">
                          <Loader2 className="w-5 h-5 animate-spin text-primary" />
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Input Area */}
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={userInput}
                      onChange={(e) => setUserInput(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && sendAIMessage()}
                      placeholder="Share your thoughts..."
                      className="flex-1 px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                      disabled={isAILoading}
                    />
                    <button
                      onClick={sendAIMessage}
                      disabled={isAILoading || !userInput.trim()}
                      className="px-4 py-3 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Send className="w-5 h-5" />
                    </button>
                  </div>

                  {perspectivesHeard.length > 0 && (
                    <p className="text-xs text-muted-foreground">
                      You've heard {perspectivesHeard.length} perspective{perspectivesHeard.length !== 1 ? 's' : ''} so far
                    </p>
                  )}
                </div>
              )}
            </div>
          </motion.section>

          {/* Location Map */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            data-testid="tree-location-map"
          >
            <h2 className="text-2xl font-heading font-bold text-primary mb-4">Location</h2>
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4 font-mono">
              <MapPin className="w-4 h-4" />
              <span>{tree.location.lat.toFixed(4)}, {tree.location.lng.toFixed(4)}</span>
            </div>
            <div className="h-64 rounded-lg overflow-hidden border border-border">
              <MapContainer
                center={[tree.location.lat, tree.location.lng]}
                zoom={10}
                style={{ height: '100%', width: '100%' }}
                scrollWheelZoom={false}
              >
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                <Marker position={[tree.location.lat, tree.location.lng]} icon={treeIcon} />
              </MapContainer>
            </div>
          </motion.section>
        </div>
      </div>
    </div>
  );
};

export default TreeDetail;